function getOrdinal(n: number) {
  if (n > 3 && n < 21) return "th";

  switch (n % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

export function parseEventDate(dateString?: string | null) {
  const value = dateString?.trim();

  if (!value) {
    return null;
  }

  const parseNativeDate = (input: string) => {
    const date = new Date(input);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const nativeDate = parseNativeDate(value);

  if (nativeDate) {
    return nativeDate;
  }

  const normalizedValue = value
    .replace(/\s+UTC$/i, "")
    .replace(
      /^(\d{4}-\d{1,2}-\d{1,2})\s+(\d{1,2}:\d{1,2}(?::\d{1,2}(?:\.\d+)?)?)/,
      "$1T$2"
    )
    .replace(/\s+(Z|[+-]\d{2}(?::?\d{2})?)$/, "$1")
    .replace(/(\.\d{3})\d+/, "$1")
    .replace(/([+-]\d{2})(\d{2})$/, "$1:$2")
    .replace(/([+-]\d{2})$/, "$1:00");

  const normalizedDate = parseNativeDate(normalizedValue);

  if (normalizedDate) {
    return normalizedDate;
  }

  const dateTimeMatch = value.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s](\d{1,2})(?::(\d{1,2})(?::(\d{1,2})(?:\.\d+)?)?)?)?(?:\s*(Z|[+-]\d{2}(?::?\d{2})?)(?:\s+UTC)?)?$/
  );

  if (!dateTimeMatch) {
    return null;
  }

  const [, year, month, day, hour = "0", minute = "0", second = "0"] =
    dateTimeMatch;
  const parsedDate = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second)
  );

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function getDateParts(date: Date, timeZone?: string | null) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      ...(timeZone ? { timeZone } : {}),
    }).formatToParts(date);

    const getPart = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? "";

    return {
      weekday: getPart("weekday"),
      day: Number(getPart("day")),
      month: getPart("month"),
      year: getPart("year"),
    };
  } catch {
    return {
      weekday: date.toLocaleDateString("en-US", { weekday: "long" }),
      day: date.getDate(),
      month: date.toLocaleDateString("en-US", { month: "long" }),
      year: String(date.getFullYear()),
    };
  }
}

export function formatEventTime(dateString: string, timeZone?: string | null) {
  const date = parseEventDate(dateString);

  if (!date) {
    return "";
  }

  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      ...(timeZone ? { timeZone } : {}),
    })
      .format(date)
      .replace(":00", "")
      .replace(/\s+/g, "")
      .toLowerCase();
  } catch {
    return date
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })
      .replace(":00", "")
      .replace(/\s+/g, "")
      .toLowerCase();
  }
}

export function formatTimeZoneLabel(
  dateString: string,
  timeZone?: string | null
) {
  const date = parseEventDate(dateString);

  if (!timeZone || !date) {
    return null;
  }

  const fallbackLabel = timeZone.replace(/_/g, " ");

  try {
    const timeZoneName = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    })
      .formatToParts(date)
      .find((part) => part.type === "timeZoneName")?.value;

    return timeZoneName ? `${timeZoneName} (${fallbackLabel})` : fallbackLabel;
  } catch {
    return fallbackLabel;
  }
}

export function formatEventTimeWithTimeZoneLabel(
  dateString: string,
  timeZone?: string | null
) {
  const eventTime = formatEventTime(dateString, timeZone);
  const timeZoneLabel = formatTimeZoneLabel(dateString, timeZone);

  return timeZoneLabel
    ? `${eventTime} (${timeZoneLabel})`.replace(/\s+/g, " ").trim()
    : eventTime.trim();
}

export default function formatDateWithOrdinal(
  dateString: string,
  timeZone?: string | null
) {
  const date = parseEventDate(dateString);

  if (!date) {
    return "";
  }

  const { weekday, day, month, year } = getDateParts(date, timeZone);

  return `${weekday} ${day}${getOrdinal(day)} ${month} ${year}`
    .replace(/\s+/g, " ")
    .trim();
}
